import React, { ChangeEvent } from 'react';
import {
  Action,
  CTAButton,
  TextField,
  Margin,
  Stack,
} from '@ovotech/element-v4';
import { FormSection } from '../FormSection/FormSection';
import { ManualAddressErrors, ManualAddressValues } from '@/src/types/MoveOut';
import { AnalyticsClick } from '@ovotech/ui-tools';
import {
  HOME_MOVES_MANUAL_ADDRESS_FIND_FROM_POSTCODE,
  HOME_MOVES_MANUAL_ADDRESS_NEXT,
} from '@/src/constants/analytics-subjects';
import styles from './ManualAddressSection.module.css';

type ManualAddressSectionProps = {
  values: ManualAddressValues;
  errors: ManualAddressErrors;
  onValuesChange: (values: ManualAddressValues) => void;
  onNext: () => void;
  onFindAddressFromPostcode: () => void;
};

export const ManualAddressSection = ({
  values,
  errors,
  onValuesChange,
  onNext,
  onFindAddressFromPostcode,
}: ManualAddressSectionProps) => {
  const changeHandler = (field: keyof ManualAddressValues) => {
    return (e: ChangeEvent<HTMLInputElement>) => {
      onValuesChange({ ...values, [field]: e.target.value });
    };
  };

  return (
    <FormSection
      id="moving-home-manual-address-section"
      className={styles.formSection}
      title="Enter your address"
    >
      <Margin top={[4, 6, 6]}>
        <Stack spaceBetween={[4, 5, 6]} className={styles.container}>
          <TextField
            id="moving-home-house-name-number"
            label="House name/number"
            fullWidth={true}
            value={values.houseNumberOrName}
            error={errors.houseNumberOrName}
            onChange={changeHandler('houseNumberOrName')}
          />
          <TextField
            id="moving-home-address-line1"
            label="Address line 1"
            fullWidth={true}
            value={values.addressLine1}
            error={errors.addressLine1}
            onChange={changeHandler('addressLine1')}
          />
          <TextField
            id="moving-home-address-line2"
            label="Address line 2 (optional)"
            fullWidth={true}
            value={values.addressLine2}
            error={errors.addressLine2}
            onChange={changeHandler('addressLine2')}
          />
          <TextField
            id="moving-home-city-town"
            label="City/Town"
            fullWidth={true}
            value={values.town}
            error={errors.town}
            onChange={changeHandler('town')}
          />
          <div className={styles.postcodeRow}>
            <TextField
              id="moving-home-postcode"
              label="Postcode"
              error={errors.postcode}
              fullWidth={true}
              value={values.postcode}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                onValuesChange({
                  ...values,
                  postcode: e.target.value.toUpperCase(),
                });
              }}
            />
          </div>
          <div>
            <AnalyticsClick name={HOME_MOVES_MANUAL_ADDRESS_FIND_FROM_POSTCODE}>
              <Action
                className={styles.findAddressFromPostcode}
                iconRight="off"
                onClick={onFindAddressFromPostcode}
              >
                Find address from postcode
              </Action>
            </AnalyticsClick>
          </div>
          <AnalyticsClick name={HOME_MOVES_MANUAL_ADDRESS_NEXT}>
            <CTAButton className={styles.nextButton} onClick={onNext}>
              Next
            </CTAButton>
          </AnalyticsClick>
        </Stack>
      </Margin>
    </FormSection>
  );
};
